import { useEffect, useMemo, useState } from 'react'
import {
  Leaf, Users, Buildings, Factory, Drop, Wind,
  ChartLineUp, Lightning, Recycle,
  ShieldCheck, ClipboardText, CalendarCheck, Bicycle, Tree, Heartbeat,
  FileCsv, FilePdf, ArrowsClockwise, CloudArrowDown
} from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { downloadCSV, openPrintWindow } from './exportHelpers'

const ACCENT = {
  coral:    '#D4614A',
  slate:    '#7A8FA0',
  olive:    '#8D9A2E',
  amber:    '#C98A2E',
  charcoal: '#4A4A4A',
}

const CARD = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '1.5rem',
  boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
  border: '1px solid #DDD5C8',
}

// Factor de emisiones eléctrico España 2024 (kg CO2e/kWh) — Red Eléctrica
const GRID_FACTOR_ES_2024 = 0.181
// Factor combustión gas natural (kg CO2e/kWh) — MITECO
const GAS_FACTOR = 0.20
// Factor genérico residuos no reciclados a vertedero (kg CO2e/kg)
const WASTE_LANDFILL_FACTOR = 0.467

// Ponderaciones medioambientales — Manual BREEAM ES NC 2015, Tabla 5 (p.18)
const BREEAM_WEIGHTS = {
  'Gestión': 0.115, 'Salud y Bienestar': 0.14, 'Energía': 0.18,
  'Transporte': 0.08, 'Agua': 0.105, 'Materiales': 0.12,
  'Residuos': 0.07, 'Uso del Suelo y Ecología': 0.095, 'Contaminación': 0.095,
}

// ── Helpers de extracción ─────────────────────────────────────────────────────

function parseJ(ev) {
  if (!ev?.justificacion) return null
  try { return JSON.parse(ev.justificacion) } catch { return null }
}

function extractENE1(evaluations) {
  const j = parseJ(evaluations?.['Energía-ENE 1'])
  if (!j?.ene1_calculadora) return null
  const calc = j.ene1_calculadora
  const zonas = (calc.zonas || []).filter(z => parseFloat(z.m2) > 0)
  let co2 = null, energy = null, m2Total = 0
  if (zonas.length > 0) {
    m2Total = zonas.reduce((s, z) => s + parseFloat(z.m2), 0)
    const wCO2 = zonas.reduce((s, z) => s + parseFloat(z.co2_eval || 0) * parseFloat(z.m2), 0) / m2Total
    const wCon = zonas.reduce((s, z) => s + parseFloat(z.con_eval || 0) * parseFloat(z.m2), 0) / m2Total
    if (wCO2 > 0) co2 = Math.round(wCO2 * 10) / 10
    if (wCon > 0) energy = Math.round(wCon * 10) / 10
  }
  return {
    eprnc: calc.eprnc != null ? Math.round(calc.eprnc * 10000) / 10000 : null,
    pts: calc.totalPts ?? calc.pts ?? null,
    co2, energy, m2Total,
    zeroCarbon: calc.zeroCarbon || false,
  }
}

function extractTRA1(evaluations) {
  // Estructura legacy: {tra1_calculadora:{ia}}. Estructura actual TRA1-DATA:
  // {tipoEdificio, autobusDedicado, nodos:[...]}. El IA se calcula fuera; si
  // no hay dato explícito devolvemos el nº de nodos como proxy de accesibilidad.
  const jLegacy = parseJ(evaluations?.['Transporte-TRA 1'])
  const iaLegacy = jLegacy?.tra1_calculadora?.ia
  if (iaLegacy != null) return { ia: Math.round(iaLegacy * 100) / 100, source: 'calc' }
  const jData = parseJ(evaluations?.['Transporte-TRA1-DATA'])
  if (jData?.nodos) return { ia: null, nodos: jData.nodos.length, source: 'nodos', tipoEdificio: jData.tipoEdificio }
  return null
}

function extractTRA3(evaluations) {
  // Legacy: {tra3_calculadora:{fulfilled}}. Actual TRA3-DATA:
  // {criterios:[{cumple}], nUsuarios, nVisitantes, ia_mejora, pct_ve}.
  const jLegacy = parseJ(evaluations?.['Transporte-TRA 3'])
  const f = jLegacy?.tra3_calculadora?.fulfilled
  if (f != null) return { fulfilled: f, source: 'legacy' }
  const jData = parseJ(evaluations?.['Transporte-TRA3-DATA'])
  if (jData?.criterios) {
    const ok = jData.criterios.filter(c => c?.cumple).length
    return { fulfilled: ok, total: jData.criterios.length, source: 'data', pct_ve: jData.pct_ve }
  }
  return null
}

/** MAT 1 — emisiones embebidas LCA (Scope 3 Cat 1) */
function extractMAT1(evaluations) {
  const j = parseJ(evaluations?.['Materiales-MAT 1'])
  const c = j?.mat1_calculadora ?? j?.mat1_oneclicklca ?? j?.oneclick_lca
  if (!c) return null
  // gwpPerM2 puede venir como total ciclo de vida (kgCO2e/m² para studyPeriod años)
  // o anualizado directamente. Preferimos anualizar si tenemos studyPeriod.
  const gwpTotal = c.gwpPerM2 ?? c.gwp_total_m2 ?? null
  const studyPeriod = c.studyPeriod ?? c.study_period ?? 50
  let co2_m2_year = c.co2_m2_year ?? c.kgco2_m2_year ?? c.gwp_m2_year ?? null
  if (co2_m2_year == null && gwpTotal != null) {
    co2_m2_year = parseFloat(gwpTotal) / parseFloat(studyPeriod || 50)
  }
  return co2_m2_year != null ? {
    co2_m2_year: parseFloat(co2_m2_year),
    gfa: c.gfa ? parseFloat(c.gfa) : null,
    studyPeriod: parseFloat(studyPeriod || 50),
    pctImprovement: c.pctImprovement ?? c.pctCalculadora ?? null,
  } : null
}

/** RSD 1 — residuos construcción */
function extractRSD1(evaluations) {
  const j = parseJ(evaluations?.['Residuos-RSD 1'])
  const c = j?.rsd1_calculadora
  if (!c) return null
  return {
    totalResiduos: c.totalResiduos ?? c.tons_total ?? null,
    tasaDesvio: c.tasaDesvio != null ? c.tasaDesvio * 100 : (c.tasa_desvio ?? c.pct ?? null),
    pct: c.pct ?? null,
    superficie: c.superficie ?? null,
    puntoEjemplar: c.puntoEjemplar ?? 0,
  }
}

/** RSD 2 — áridos reciclados */
function extractRSD2(evaluations) {
  const j = parseJ(evaluations?.['Residuos-RSD 2'])
  const c = j?.rsd2_calculadora
  if (!c) return null
  return {
    pct_reciclado: c.pct_reciclado ?? null,
    total_m3: c.total_m3 ?? null,
    reciclado_m3: c.aridos_no_ligados_reciclado_m3 ?? null,
  }
}

/** CNT 1 — refrigerantes EDCV */
function extractCNT1(evaluations) {
  const j = parseJ(evaluations?.['Contaminación-CNT 1'])
  const c = j?.cont1_calculadora
  if (!c) return null
  return {
    edcv: c.edcvTotal ?? null,
    pts: c.pts ?? null,
    opcion: c.opcion ?? null,
    sistemas: (c.sistemas || []).length,
  }
}

/** AG 1 — eficiencia hídrica */
function extractAG1(evaluations) {
  const j = parseJ(evaluations?.['Agua-AG 1'])
  const c = j?.ag1_calculadora
  if (!c) return null
  return {
    nivelGlobal: c.nivelGlobal ?? null,
    levelKey: c.levelKey ?? null,
    buildingType: c.buildingType ?? null,
    sistemaAguas: c.sistemaAguas ?? null,
  }
}

function catPct(evaluations, BREEAM_CRITERIA, ...catNames) {
  let obtained = 0, max = 0
  catNames.forEach(catName => {
    const cat = BREEAM_CRITERIA?.[catName]
    if (!cat) return
    Object.entries(cat.criteria).forEach(([code, c]) => {
      const ev = evaluations?.[`${catName}-${code}`]
      // Descontar criterios marcados N/A en la justificación
      let isNA = false
      try { isNA = !!JSON.parse(ev?.justificacion || 'null')?.no_aplica } catch {}
      if (isNA) return
      max += (ev?.points_max ?? c.points ?? 0)
      obtained += (ev?.puntos_auditoria ?? ev?.points_obtained ?? 0)
    })
  })
  return max === 0 ? null : { pct: Math.round((obtained / max) * 100), obtained, max }
}

/** Puntuación BREEAM real (ponderada Tabla 5 + bonus INO) — idéntica fórmula que el dashboard */
function computeBreeamScore(evaluations, BREEAM_CRITERIA) {
  let weightedTotal = 0
  Object.entries(BREEAM_CRITERIA || {}).forEach(([cat, data]) => {
    if (cat === 'Innovación') {
      // INO: +1% por punto, sin ponderar
      const pts = Object.keys(data.criteria).reduce((s, code) => s + (((evaluations?.[`${cat}-${code}`]?.puntos_auditoria) ?? (evaluations?.[`${cat}-${code}`]?.points_obtained) ?? 0)), 0)
      weightedTotal += pts
      return
    }
    const c = catPct(evaluations, BREEAM_CRITERIA, cat)
    if (!c || c.max === 0) return
    const w = BREEAM_WEIGHTS[cat] || 0
    weightedTotal += (c.obtained / c.max) * w * 100
  })
  return Math.round(weightedTotal * 10) / 10  // 1 decimal
}

/** Agrega lecturas anuales de building_sensors */
function aggregateSensors(rows) {
  if (!rows || rows.length === 0) return null
  const byYear = new Map()
  rows.forEach(r => {
    const y = r.reading_year
    const acc = byYear.get(y) || {
      year: y, months: 0,
      energy_kwh: 0, energy_renew_kwh: 0, electricity_kwh: 0, gas_kwh: 0,
      water_m3: 0, water_reclaimed_m3: 0,
      waste_t: 0, waste_recycled_t: 0, waste_landfill_t: 0, waste_hazardous_t: 0,
      scope1: 0, scope2: 0, scope3: 0,
      iaq_co2: 0, iaq_pm25: 0, iaq_n: 0,
    }
    acc.months += 1
    // Si energy_kwh_total no se rellenó, sumar componentes
    const totalThisMonth = +(r.energy_kwh_total || 0) || (
      +(r.electricity_kwh || 0) + +(r.gas_kwh || 0) + +(r.district_heat_kwh || 0) + +(r.energy_kwh_renewable || 0)
    )
    acc.energy_kwh += totalThisMonth
    acc.energy_renew_kwh += +(r.energy_kwh_renewable || 0)
    acc.electricity_kwh += +(r.electricity_kwh || 0)
    acc.gas_kwh += +(r.gas_kwh || 0)
    acc.water_m3 += +(r.water_m3_total || 0)
    acc.water_reclaimed_m3 += +(r.water_m3_reclaimed || 0)
    acc.waste_t += +(r.waste_tons_total || 0)
    acc.waste_recycled_t += +(r.waste_tons_recycled || 0)
    acc.waste_landfill_t += +(r.waste_tons_landfill || 0)
    acc.waste_hazardous_t += +(r.waste_tons_hazardous || 0)
    acc.scope1 += +(r.scope1_kgco2e || 0)
    acc.scope2 += +(r.scope2_kgco2e || 0)
    acc.scope3 += +(r.scope3_kgco2e || 0)
    if (r.iaq_co2_ppm_avg) { acc.iaq_co2 += +r.iaq_co2_ppm_avg; acc.iaq_n += 1 }
    if (r.iaq_pm25_avg) acc.iaq_pm25 += +r.iaq_pm25_avg
    byYear.set(y, acc)
  })
  // Devuelve año más reciente con al menos 1 lectura
  const years = [...byYear.keys()].sort((a, b) => b - a)
  if (years.length === 0) return null
  const y = byYear.get(years[0])
  if (y.iaq_n > 0) y.iaq_co2 = y.iaq_co2 / y.iaq_n
  return y
}

// ── UI ────────────────────────────────────────────────────────────────────────

function TrendArrow({ direction }) {
  if (direction === 'up')   return <span style={{ color: ACCENT.olive, fontWeight: '600', fontSize: '0.8rem' }}>↑</span>
  if (direction === 'down') return <span style={{ color: ACCENT.coral, fontWeight: '600', fontSize: '0.8rem' }}>↓</span>
  return <span style={{ color: '#B0A89E', fontSize: '0.8rem' }}>—</span>
}

const MONO = "'IBM Plex Mono', monospace"

function StatusBadge({ tag }) {
  const styles = tag === 'sensor'
    ? { bg: '#E3EEDF', color: '#2E6E3F', label: 'SENSOR' }
    : tag === 'real'
      ? { bg: '#EAF3EC', color: '#3B7A50', label: 'DATO REAL' }
      : { bg: '#FDF8EE', color: '#7A4A10', label: 'PENDIENTE' }
  return (
    <span style={{
      fontSize: '0.55rem', background: styles.bg, color: styles.color,
      borderRadius: '3px', padding: '2px 6px', fontWeight: '600',
      whiteSpace: 'nowrap', letterSpacing: '0.03em',
      alignSelf: 'flex-start', flexShrink: 0,
    }}>{styles.label}</span>
  )
}

function PhaseRow({ phase, value, unit, note, trend, empty }) {
  const isEmpty = empty || value == null || value === '—'
  const phaseColor = phase === 'design'
    ? { bg: '#FBF7EF', border: '#D9C795', chip: '#7A6338', label: 'DISEÑO / CONSTRUCCIÓN' }
    : { bg: '#F1F6EE', border: '#8FB598', chip: '#2E6E3F', label: 'OPERACIÓN' }
  return (
    <div style={{
      padding: '0.5rem 0.65rem',
      background: isEmpty ? 'rgba(240, 235, 226, 0.35)' : phaseColor.bg,
      borderRadius: '5px',
      borderLeft: `3px solid ${isEmpty ? '#D8D0C0' : phaseColor.border}`,
      display: 'flex', flexDirection: 'column', gap: '0.2rem',
      minHeight: '58px',
    }}>
      <div style={{
        fontSize: '0.55rem', fontWeight: '700', letterSpacing: '0.06em',
        color: isEmpty ? '#9A8A7A' : phaseColor.chip,
      }}>{phaseColor.label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.3rem', flexWrap: 'wrap' }}>
        <span style={{
          fontSize: isEmpty ? '1rem' : '1.2rem', fontWeight: '700',
          fontFamily: MONO,
          color: isEmpty ? '#B0A89E' : '#2C2017',
          lineHeight: 1.15,
        }}>{isEmpty ? '—' : value}</span>
        {unit && <span style={{ fontSize: '0.65rem', color: '#7A6A5A' }}>{unit}</span>}
        {!isEmpty && trend && <TrendArrow direction={trend} />}
      </div>
      {note && <div style={{
        fontSize: '0.62rem', lineHeight: 1.3,
        color: isEmpty ? '#B0A89E' : '#7A6A5A',
        fontStyle: isEmpty ? 'italic' : 'normal',
      }}>{note}</div>}
    </div>
  )
}

function IndicatorCard({ icon: Icon, label, value, unit, benchmark, trend, note, tag, code, design, operation }) {
  const isDual = !!(design || operation)
  // En modo dual, deducir tag desde design/operation si el padre no lo pasó
  let effectiveTag = tag
  if (isDual && !effectiveTag) {
    if (operation != null) effectiveTag = 'sensor'
    else if (design != null) effectiveTag = 'real'
  }
  const isReal = effectiveTag === 'real'
  const isSensor = effectiveTag === 'sensor'
  const borderColor = isReal || isSensor ? '#C8D8C0' : '#DDD5C8'
  // Ajustar tamaño si el value simple es texto largo (p.ej. FINALIZADA)
  const isLongText = typeof value === 'string' && value.length > 7 && isNaN(parseFloat(value))
  return (
    <div style={{
      background: '#FAF7F3',
      border: `1px solid ${borderColor}`,
      borderRadius: '8px',
      padding: '0.9rem 1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      minHeight: isDual ? '210px' : '155px',
    }}>
      {/* Header — altura fija */}
      <div style={{
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', gap: '0.5rem',
      }}>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
          <Icon size={16} style={{ marginTop: '2px', color: '#7A6A5A', flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: '0.78rem', fontWeight: '500', color: '#5C4A3A',
              lineHeight: 1.25,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>{label}</div>
            {code && <div style={{
              fontSize: '0.58rem', color: '#9A8A7A', fontFamily: MONO,
              letterSpacing: '0.02em', marginTop: '2px',
            }}>{code}</div>}
          </div>
        </div>
        <StatusBadge tag={effectiveTag} />
      </div>

      {/* Cuerpo */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flex: 1 }}>
        {isDual ? (
          <>
            <PhaseRow phase="design"
              value={design?.value} unit={design?.unit ?? unit}
              note={design?.note ?? (design == null ? 'Sin datos de proyecto' : undefined)}
              trend={design?.trend}
              empty={design == null} />
            <PhaseRow phase="operation"
              value={operation?.value} unit={operation?.unit ?? unit}
              note={operation?.note ?? (operation == null ? 'Sin sensores IoT' : undefined)}
              trend={operation?.trend}
              empty={operation == null} />
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: isLongText ? '1.15rem' : '1.5rem',
              fontWeight: '700', fontFamily: MONO, color: '#2C2017',
              lineHeight: 1.1,
            }}>{value}</span>
            {unit && <span style={{ fontSize: '0.7rem', color: '#7A6A5A' }}>{unit}</span>}
            <TrendArrow direction={trend} />
          </div>
        )}
        {note && !isDual && <div style={{
          fontSize: '0.68rem', color: '#8A7A6A', fontStyle: 'italic', lineHeight: 1.3,
        }}>{note}</div>}
      </div>

      {/* Ref pegado al pie */}
      {benchmark && <div style={{
        fontSize: '0.65rem', color: '#7A6A5A',
        borderTop: '1px solid #EEE8DE',
        paddingTop: '0.4rem',
        marginTop: 'auto',
        lineHeight: 1.3,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>Ref: {benchmark}</div>}
    </div>
  )
}

function PillarHeader({ letter, title, icon: Icon, accentColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', paddingBottom: '0.875rem', borderBottom: '1px solid #DDD5C8', marginBottom: '1rem' }}>
      <div style={{ width: '36px', height: '36px', background: '#EDE7DE', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} style={{ color: accentColor || '#7A6A5A' }} />
      </div>
      <div>
        <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Pilar {letter}</div>
        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#2C2017' }}>{title}</div>
      </div>
    </div>
  )
}

function HeaderButton({ icon: Icon, label, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: 'rgba(245, 240, 234, 0.1)', border: '1px solid rgba(245, 240, 234, 0.3)',
      color: '#F5F0EA', borderRadius: '5px', padding: '6px 11px',
      fontSize: '0.72rem', fontWeight: '500', cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.45 : 1, display: 'inline-flex', alignItems: 'center', gap: '5px',
    }}>
      <Icon size={13} /> {label}
    </button>
  )
}

// ── Componente principal ─────────────────────────────────────────────────────

export default function ReportESG({ project, BREEAM_CRITERIA }) {
  const evaluations = project?.evaluations || {}
  const totalPoints = project?.totalPoints || 0
  const maxPoints = project?.maxPoints || 110
  // Puntuación BREEAM ponderada (Tabla 5 + INO) — coincide con el dashboard oficial.
  // Usamos breeamPctExact (1 decimal) para no cruzar accidentalmente los
  // umbrales de la Tabla 3 del manual al redondear (55 % → Muy Bueno,
  // 70 % → Excelente, etc.).
  const breeamPctExact = computeBreeamScore(evaluations, BREEAM_CRITERIA)

  // Conjunto de claves válidas según el esquema actual ("Categoría-Código")
  const validCriteriaKeys = new Set()
  Object.entries(BREEAM_CRITERIA || {}).forEach(([cat, data]) => {
    Object.keys(data.criteria).forEach(code => validCriteriaKeys.add(`${cat}-${code}`))
  })
  const totalCriteria = validCriteriaKeys.size
  // Cuenta solo evaluaciones cuyo criterio sigue existiendo (descarta obsoletos)
  const evaluatedCount = Object.keys(evaluations).filter(k => validCriteriaKeys.has(k)).length
  const evidencesPct = totalCriteria > 0 ? Math.min(100, Math.round((evaluatedCount / totalCriteria) * 100)) : 0

  // Certificación finalizada: si alguna evaluación tiene puntos_auditoria del
  // certificador BREEAM externo, el proyecto pasó por auditoría oficial y el
  // informe final ya se emitió. Es la señal de que la certificación está cerrada.
  const isCertificationFinalized = Object.values(evaluations).some(
    ev => ev?.puntos_auditoria != null
  )
  const auditFecha = Object.values(evaluations)
    .map(ev => ev?.fecha_auditoria)
    .filter(Boolean)
    .sort()
    .pop() || null

  // ── State: sensores building_sensors ──
  const [sensors, setSensors] = useState(null)
  const [sensorsLoading, setSensorsLoading] = useState(false)
  const [sensorsErr, setSensorsErr] = useState(null)
  const [iros, setIros] = useState([])

  const loadSensors = async () => {
    if (!project?.id) return
    setSensorsLoading(true); setSensorsErr(null)
    const [{ data, error }, { data: iroRows }] = await Promise.all([
      supabase.from('building_sensors').select('*').eq('project_id', project.id)
        .order('reading_year', { ascending: false }).order('reading_month', { ascending: false }),
      supabase.from('operational_iros').select('*').eq('project_id', project.id).neq('status', 'dismissed').neq('status', 'closed'),
    ])
    setSensorsLoading(false)
    if (error) { setSensorsErr(error.message); return }
    setSensors(aggregateSensors(data || []))
    setIros(iroRows || [])
  }

  // loadSensors depende solo de project?.id (ya en deps).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSensors() }, [project?.id])

  // ── Datos reales de calculadores ──
  const ene1 = extractENE1(evaluations)
  const tra1IA = extractTRA1(evaluations)
  const tra3Opt = extractTRA3(evaluations)
  const mat1 = extractMAT1(evaluations)
  const rsd1 = extractRSD1(evaluations)
  const rsd2 = extractRSD2(evaluations)
  const cnt1 = extractCNT1(evaluations)
  const ag1 = extractAG1(evaluations)

  // ── Scope 1/2/3 ──
  // Preferencia: sensor anual. Fallback: derivado de calculadores.
  const scope = useMemo(() => {
    // m² para Scope 2 = el de ENE 1 (zonas acondicionadas); para Scope 3 = gfa MAT 1 (total proyecto)
    const m2Op = ene1?.m2Total || project?.surface_m2 || null
    const m2Lca = mat1?.gfa || m2Op || null
    let s1 = null, s2 = null, s3 = null, source = 'sensor'
    if (sensors && sensors.scope1 > 0) s1 = sensors.scope1 / 1000  // → tCO2e
    if (sensors && sensors.scope2 > 0) s2 = sensors.scope2 / 1000
    if (sensors && sensors.scope3 > 0) s3 = sensors.scope3 / 1000
    if (s1 == null && sensors?.gas_kwh > 0) s1 = (sensors.gas_kwh * GAS_FACTOR) / 1000
    if (s2 == null && sensors?.electricity_kwh > 0) s2 = (sensors.electricity_kwh * GRID_FACTOR_ES_2024) / 1000
    if (s1 == null && s2 == null && ene1?.co2 != null && m2Op) {
      s2 = (ene1.co2 * m2Op) / 1000
      source = 'estimated'
    }
    if (s3 == null && mat1?.co2_m2_year != null && m2Lca) {
      s3 = (mat1.co2_m2_year * m2Lca) / 1000
      source = source === 'sensor' ? 'mixed' : 'estimated'
    }
    const total = (s1 || 0) + (s2 || 0) + (s3 || 0)
    return { s1, s2, s3, total: total || null, source, m2: m2Op, m2Lca }
  }, [sensors, ene1, mat1, project?.surface_m2])

  // ── Proxies BREEAM ──
  const aguaPct = catPct(evaluations, BREEAM_CRITERIA, 'Agua')
  const cirPct = catPct(evaluations, BREEAM_CRITERIA, 'Materiales', 'Residuos')
  const usePct = catPct(evaluations, BREEAM_CRITERIA, 'Uso del Suelo y Ecología')
  const sybPct = catPct(evaluations, BREEAM_CRITERIA, 'Salud y Bienestar')
  const traPct = catPct(evaluations, BREEAM_CRITERIA, 'Transporte')
  const conPct = catPct(evaluations, BREEAM_CRITERIA, 'Contaminación')
  const gestPct = catPct(evaluations, BREEAM_CRITERIA, 'Gestión')

  const catsDone = Object.entries(BREEAM_CRITERIA || {}).filter(([cat, data]) => {
    const codes = Object.keys(data.criteria)
    return codes.length > 0 && codes.every(code => evaluations[`${cat}-${code}`])
  }).length
  const catsTotal = Object.keys(BREEAM_CRITERIA || {}).length

  // Clasificación BREEAM ES NC 2015 — Tabla 3 manual (p.16).
  // Comparamos contra breeamPctExact (valor real con decimales) para no
  // cruzar los umbrales al redondear.
  const rating =
    breeamPctExact >= 85 ? 'Excepcional' :
    breeamPctExact >= 70 ? 'Excelente' :
    breeamPctExact >= 55 ? 'Muy Bueno' :
    breeamPctExact >= 45 ? 'Bueno' :
    breeamPctExact >= 30 ? 'Correcto' : 'Sin Clasificar'
  const ratingColor =
    breeamPctExact >= 70 ? ACCENT.olive :
    breeamPctExact >= 45 ? ACCENT.amber : '#B0A89E'

  // ── Tabla maestra de indicadores para CSV/PDF ──
  const indicators = useMemo(() => {
    const fmt = (v, d = 2) => v == null ? null : Math.round(v * 10 ** d) / 10 ** d
    return [
      // E — Emisiones (GRI 305 / GHG Protocol)
      { framework: 'GHG/GRI', code: 'GRI 305-1', name: 'Scope 1 — Emisiones directas (combustión in situ)', value: fmt(scope.s1), unit: 'tCO2e/año', source: scope.s1 != null ? scope.source : '—' },
      { framework: 'GHG/GRI', code: 'GRI 305-2', name: 'Scope 2 — Emisiones indirectas (electricidad comprada)', value: fmt(scope.s2), unit: 'tCO2e/año', source: scope.s2 != null ? scope.source : '—' },
      { framework: 'GHG/GRI', code: 'GRI 305-3', name: 'Scope 3 — Otras emisiones indirectas (Cat. 1 materiales + Cat. 5 residuos)', value: fmt(scope.s3), unit: 'tCO2e/año', source: scope.s3 != null ? scope.source : '—' },
      { framework: 'GHG/GRI', code: 'GRI 305-4', name: 'Intensidad de emisiones', value: fmt(scope.total != null && scope.m2 ? (scope.total * 1000 / scope.m2) : null), unit: 'kgCO2e/m²·año', source: scope.source },
      // E — Energía (GRI 302)
      { framework: 'GRI', code: 'GRI 302-1', name: 'Consumo energético total dentro de la organización', value: fmt(sensors?.energy_kwh), unit: 'kWh/año', source: 'sensor' },
      { framework: 'GRI', code: 'GRI 302-1', name: 'Consumo eléctrico', value: fmt(sensors?.electricity_kwh), unit: 'kWh/año', source: 'sensor' },
      { framework: 'GRI', code: 'GRI 302-1', name: 'Consumo gas natural', value: fmt(sensors?.gas_kwh), unit: 'kWh/año', source: 'sensor' },
      { framework: 'GRI', code: 'GRI 302-1', name: 'Energía renovable in situ', value: fmt(sensors?.energy_renew_kwh), unit: 'kWh/año', source: 'sensor' },
      { framework: 'GRI', code: 'GRI 302-3', name: 'Intensidad energética', value: fmt(ene1?.energy), unit: 'kWh/m²·año', source: ene1?.energy != null ? 'ENE 1 calculadora' : '—' },
      { framework: 'BREEAM', code: 'ENE 1 EPRNC', name: 'Eficiencia energética EPRNC', value: ene1?.eprnc, unit: '', source: 'ENE 1 calculadora' },
      // E — Agua (GRI 303)
      { framework: 'GRI', code: 'GRI 303-3', name: 'Captación de agua', value: fmt(sensors?.water_m3), unit: 'm³/año', source: 'sensor' },
      { framework: 'GRI', code: 'GRI 303-4', name: 'Agua reutilizada/reciclada', value: fmt(sensors?.water_reclaimed_m3), unit: 'm³/año', source: 'sensor' },
      { framework: 'BREEAM', code: 'AG proxy', name: 'Eficiencia hídrica BREEAM', value: aguaPct?.pct, unit: '% pts', source: 'BREEAM' },
      // E — Residuos (GRI 306)
      { framework: 'GRI', code: 'GRI 306-3', name: 'Residuos generados (operación)', value: fmt(sensors?.waste_t), unit: 't/año', source: 'sensor' },
      { framework: 'GRI', code: 'GRI 306-4', name: 'Residuos desviados de vertedero', value: fmt(sensors?.waste_recycled_t), unit: 't/año', source: 'sensor' },
      { framework: 'GRI', code: 'GRI 306-5', name: 'Residuos a vertedero', value: fmt(sensors?.waste_landfill_t), unit: 't/año', source: 'sensor' },
      { framework: 'BREEAM', code: 'RSD 1', name: 'Residuos construcción desviados de vertedero', value: rsd1?.tons_desviadas, unit: 't', source: 'RSD 1 calculadora' },
      { framework: 'BREEAM', code: 'RSD 1', name: 'Tasa de desvío residuos construcción', value: rsd1?.tasa_desvio, unit: '%', source: 'RSD 1 calculadora' },
      // E — Biodiversidad (GRI 304)
      { framework: 'BREEAM', code: 'USE proxy', name: 'Biodiversidad y ecología BREEAM', value: usePct?.pct, unit: '% pts', source: 'BREEAM' },
      // E — Contaminación
      { framework: 'BREEAM', code: 'CONT proxy', name: 'Prevención contaminación BREEAM', value: conPct?.pct, unit: '% pts', source: 'BREEAM' },
      // S — Social
      { framework: 'GRI', code: 'GRI 403-6', name: 'Salud y bienestar ocupantes BREEAM', value: sybPct?.pct, unit: '% pts', source: 'BREEAM' },
      { framework: 'BREEAM', code: 'TRA 1', name: 'Índice de accesibilidad', value: tra1IA?.ia ?? tra1IA?.nodos ?? null, unit: tra1IA?.ia != null ? '' : 'nodos', source: 'TRA 1 calculadora' },
      { framework: 'BREEAM', code: 'TRA 3', name: 'Movilidad alternativa (opciones cumplidas)', value: tra3Opt?.fulfilled ?? null, unit: tra3Opt?.total ? `/${tra3Opt.total}` : '/5', source: 'TRA 3 calculadora' },
      { framework: 'IAQ', code: 'CO2 interior', name: 'CO₂ medio interior (sensores)', value: fmt(sensors?.iaq_co2, 0), unit: 'ppm', source: 'sensor' },
      // G — Gobernanza
      { framework: 'BREEAM', code: 'GST proxy', name: 'Gestión del proyecto BREEAM', value: gestPct?.pct, unit: '% pts', source: 'BREEAM' },
      { framework: 'BREEAM', code: 'Score', name: 'Puntuación BREEAM total', value: breeamPctExact.toFixed(1), unit: '%', source: 'BREEAM' },
      { framework: 'BREEAM', code: 'Expediente', name: 'Cobertura del expediente', value: evidencesPct, unit: '%', source: 'BREEAM' },
    ]
  }, [scope, sensors, ene1, rsd1, aguaPct, usePct, sybPct, conPct, gestPct, tra1IA, tra3Opt, breeamPctExact, evidencesPct])

  const handleCSV = () => {
    const rows = indicators.map(i => ({
      framework: i.framework, code: i.code, indicator: i.name,
      value: i.value != null ? i.value : '', unit: i.unit || '', source: i.source || '',
    }))
    const fname = `ReportESG_${(project?.name || 'proyecto').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`
    downloadCSV(rows, fname)
  }

  const handlePDF = () => {
    const byFw = {}
    indicators.forEach(i => {
      const k = i.framework
      if (!byFw[k]) byFw[k] = []
      byFw[k].push([i.code, i.name, i.value != null ? i.value : '—', i.unit || '', i.source || '—'])
    })
    const order = ['GHG/GRI', 'GRI', 'BREEAM', 'IAQ']
    const sections = []
    sections.push({
      title: 'Resumen ejecutivo',
      cols: ['Indicador', 'Valor'],
      rows: [
        ['BREEAM ES NC 2015 — Puntuación', `${breeamPctExact.toFixed(1)}%`],
        ['Clasificación BREEAM', rating],
        ['Cobertura expediente', `${evidencesPct}%`],
        ['Emisiones totales', scope.total != null ? `${(Math.round(scope.total * 10) / 10)} tCO₂e/año` : '—'],
        ['Fuente sensores', sensors ? `año ${sensors.year} · ${sensors.months} lecturas` : 'sin lecturas en building_sensors'],
      ],
    })
    Object.keys(byFw).sort((a, b) => order.indexOf(a) - order.indexOf(b)).forEach(fw => {
      sections.push({
        title: `Indicadores ${fw}`,
        subtitle: fw === 'GRI' ? 'GRI Standards 302/303/305/306/403' : fw === 'GHG/GRI' ? 'GHG Protocol Scope 1/2/3 + GRI 305' : null,
        cols: ['Código', 'Indicador', 'Valor', 'Unidad', 'Fuente'],
        rows: byFw[fw],
      })
    })
    openPrintWindow({
      title: 'Report ESG',
      projectName: project?.name || 'Proyecto',
      sections,
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 2rem', color: '#F5F0EA', display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: '500' }}>
            Report ESG · {project?.name || 'Proyecto'}
          </div>
          <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#F5F0EA' }}>Dashboard de Indicadores ESG</div>
          <div style={{ fontSize: '0.8rem', color: '#B0A89E', marginTop: '0.2rem' }}>BREEAM ES NC 2015 + GRI 302/303/305/306 + GHG Protocol Scope 1/2/3</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: '0.75rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Clasificación BREEAM', value: rating,            color: ratingColor, isText: true },
            { label: 'BREEAM Score',         value: `${breeamPctExact.toFixed(1)}%`, color: '#F5F0EA' },
            { label: 'Expediente',           value: `${evidencesPct}%`, color: ACCENT.amber },
          ].map((item, i) => (
            <div key={i} style={{
              minWidth: '110px',
              padding: '0.4rem 0.8rem',
              background: 'rgba(245, 240, 234, 0.06)',
              border: '1px solid rgba(245, 240, 234, 0.12)',
              borderRadius: '6px',
              display: 'flex', flexDirection: 'column', justifyContent: 'center',
              alignItems: 'center', gap: '0.2rem',
            }}>
              <div style={{
                fontSize: item.isText ? '0.95rem' : '1.5rem',
                fontWeight: '700', fontFamily: MONO,
                color: item.color, lineHeight: 1.1,
                letterSpacing: item.isText ? '0.02em' : 0,
                textAlign: 'center',
              }}>{item.value}</div>
              <div style={{ fontSize: '0.6rem', color: '#B0A89E', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{item.label}</div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <HeaderButton icon={ArrowsClockwise} label={sensorsLoading ? 'Cargando…' : 'Refrescar sensors'} onClick={loadSensors} disabled={sensorsLoading} />
            <HeaderButton icon={FileCsv} label="CSV" onClick={handleCSV} />
            <HeaderButton icon={FilePdf} label="PDF" onClick={handlePDF} />
          </div>
        </div>
      </div>

      {/* Estado sensors */}
      <div style={{ background: sensors ? '#F0F5EC' : '#FAF7F3', border: `1px solid ${sensors ? '#C8D8C0' : '#DDD5C8'}`, borderRadius: '6px', padding: '0.7rem 1rem', display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.78rem', color: '#5C4A3A' }}>
        <CloudArrowDown size={16} style={{ color: sensors ? '#3B7A50' : '#7A6A5A' }} />
        {sensorsLoading ? 'Cargando lecturas de building_sensors…'
          : sensorsErr ? <span style={{ color: ACCENT.coral }}>Error: {sensorsErr}</span>
          : sensors ? <span><strong>Sensores activos:</strong> año {sensors.year} con {sensors.months} lecturas mensuales. Energía {Math.round(sensors.energy_kwh).toLocaleString()} kWh · Agua {Math.round(sensors.water_m3).toLocaleString()} m³ · Residuos {sensors.waste_t.toFixed(1)} t.</span>
          : <span><strong>Sin lecturas en building_sensors</strong>. Carga datos mensuales en la pestaña de monitorización para activar los indicadores SENSOR (Scope 1/2, agua y residuos operacionales).</span>}
      </div>

      {/* IROs operativos detectados */}
      {iros.length > 0 && (() => {
        const impact = iros.filter(i => i.type === 'impact')
        const risk   = iros.filter(i => i.type === 'risk')
        const opp    = iros.filter(i => i.type === 'opportunity')
        const totalEur = iros.reduce((s, i) => s + (Number(i.financial_estimate_eur) || 0), 0)
        return (
          <div style={{ ...CARD, borderLeft: `3px solid ${ACCENT.olive}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.6rem' }}>
              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#2C2017' }}>Eventos operacionales (IROs)</div>
                <div style={{ fontSize: '0.72rem', color: '#7A6A5A' }}>Detectados por el módulo Monitorización → alimentan CSRD E1-3/E1-6/E1-9 y GRESB RM.</div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ background: '#FDF5F3', border: `1px solid ${ACCENT.coral}55`, borderRadius: 4, padding: '0.25rem 0.55rem', fontSize: '0.72rem', color: ACCENT.coral, fontWeight: 600 }}>{impact.length} Impactos</span>
                <span style={{ background: '#FFF6E5', border: `1px solid ${ACCENT.amber}55`, borderRadius: 4, padding: '0.25rem 0.55rem', fontSize: '0.72rem', color: ACCENT.amber, fontWeight: 600 }}>{risk.length} Riesgos</span>
                <span style={{ background: '#F0F5EC', border: `1px solid ${ACCENT.olive}55`, borderRadius: 4, padding: '0.25rem 0.55rem', fontSize: '0.72rem', color: ACCENT.olive, fontWeight: 600 }}>{opp.length} Oportunidades</span>
                {totalEur > 0 && <span style={{ background: '#F5F0EA', border: `1px solid #DDD5C8`, borderRadius: 4, padding: '0.25rem 0.55rem', fontSize: '0.72rem', color: '#2C2017', fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{Math.round(totalEur).toLocaleString('es-ES')} €/año</span>}
              </div>
            </div>
            <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
                  <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Detector</th>
                  <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>ESRS</th>
                  <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Tipo</th>
                  <th style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>Severidad</th>
                </tr>
              </thead>
              <tbody>
                {iros.map((r, i) => {
                  const col = r.type === 'impact' ? ACCENT.coral : r.type === 'risk' ? ACCENT.amber : ACCENT.olive
                  const lbl = r.type === 'impact' ? 'Impacto' : r.type === 'risk' ? 'Riesgo' : 'Oportunidad'
                  return (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? '#FFF' : '#FAF7F3', borderBottom: '1px solid #F0EBE2' }}>
                      <td style={{ padding: '0.3rem 0.5rem' }}>{(r.detector_id || '').replace(/^D\d+\./, '').replace(/_/g, ' ')}</td>
                      <td style={{ padding: '0.3rem 0.5rem', fontFamily: "'IBM Plex Mono', monospace", color: '#7A6A5A' }}>{r.esrs_datapoint || '—'}</td>
                      <td style={{ padding: '0.3rem 0.5rem', color: col, fontWeight: 600 }}>{lbl}</td>
                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", color: col }}>{'●'.repeat(r.severity || 0)}{'○'.repeat(5 - (r.severity || 0))}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      })()}

      {/* E — Ambiental */}
      <div style={{ ...CARD, borderLeft: `3px solid ${ACCENT.olive}` }}>
        <PillarHeader letter="E" title="Ambiental" icon={Leaf} accentColor={ACCENT.olive} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.875rem' }}>

          <IndicatorCard icon={Factory} label="Scope 1 — Combustión in situ"
            benchmark="Edificio bajo en carbono < 5 tCO₂e/año"
            code="GRI 305-1 · GHG Scope 1"
            design={ene1?.zeroCarbon
              ? { value: '0.00', unit: 'tCO₂e/año', note: 'ENE 1: edificio CO₂ nulo (sin combustión)', trend: 'up' }
              : (ene1?.co2 != null
                ? { value: '—', unit: 'tCO₂e/año', note: 'Diseño no separa gas de electricidad (ver Scope 2)' }
                : null)}
            operation={sensors?.gas_kwh > 0
              ? { value: ((sensors.gas_kwh * GAS_FACTOR) / 1000).toFixed(2), unit: 'tCO₂e/año',
                  note: `Sensor gas · año ${sensors.year} · factor ${GAS_FACTOR} kgCO₂/kWh`,
                  trend: (sensors.gas_kwh * GAS_FACTOR / 1000) < 5 ? 'up' : 'down' }
              : null} />

          <IndicatorCard icon={Lightning} label="Scope 2 — Electricidad comprada"
            benchmark={`Factor red ES 2024: ${GRID_FACTOR_ES_2024} kgCO₂/kWh`}
            code="GRI 305-2 · GHG Scope 2"
            design={ene1?.co2 != null && scope.m2
              ? { value: ((ene1.co2 * scope.m2) / 1000).toFixed(2), unit: 'tCO₂e/año',
                  note: `ENE 1 · ${ene1.co2} kgCO₂/m²·año × ${Math.round(scope.m2)} m²`, trend: 'up' }
              : null}
            operation={sensors?.electricity_kwh > 0
              ? { value: ((sensors.electricity_kwh * GRID_FACTOR_ES_2024) / 1000).toFixed(2), unit: 'tCO₂e/año',
                  note: `Sensor eléctrico · año ${sensors.year}`, trend: 'up' }
              : null} />

          <IndicatorCard icon={Recycle} label="Scope 3 — Materiales + Residuos"
            benchmark="Cat. 1 (Goods) + Cat. 5 (Waste) GHG Protocol"
            code="GRI 305-3 · GHG Scope 3 (Cat 1+5)"
            design={mat1?.co2_m2_year != null && (mat1.gfa || scope.m2)
              ? { value: ((mat1.co2_m2_year * (mat1.gfa || scope.m2)) / 1000).toFixed(2), unit: 'tCO₂e/año',
                  note: `MAT 1 LCA · ${mat1.co2_m2_year.toFixed(1)} kgCO₂/m²·año (anualizado ${mat1.studyPeriod} años)`, trend: 'up' }
              : null}
            operation={sensors?.scope3 > 0
              ? { value: (sensors.scope3 / 1000).toFixed(2), unit: 'tCO₂e/año', note: `Sensores año ${sensors.year}`, trend: 'up' }
              : (sensors?.waste_landfill_t > 0
                ? { value: (sensors.waste_landfill_t * WASTE_LANDFILL_FACTOR).toFixed(2), unit: 'tCO₂e/año',
                    note: `Estimado desde residuos vertedero (año ${sensors.year})`, trend: 'up' }
                : null)} />

          <IndicatorCard icon={Factory} label="Emisiones operación (kgCO₂/m²·año)"
            benchmark="< 40 kgCO₂/m²·año (CTE-HE0 nZEB)"
            code="BREEAM ENE 1 · GRI 305-4"
            design={ene1?.co2 != null
              ? { value: ene1.co2.toFixed(1), unit: 'kgCO₂/m²·año',
                  note: ene1.zeroCarbon ? '✓ ENE 1: CO₂ nulo' : 'Simulación energética ENE 1',
                  trend: ene1.co2 < 40 ? 'up' : 'down' }
              : null}
            operation={sensors && scope.m2 && (sensors.scope1 + sensors.scope2) > 0
              ? { value: ((sensors.scope1 + sensors.scope2) / scope.m2).toFixed(1), unit: 'kgCO₂/m²·año',
                  note: `Sensores año ${sensors.year} · Scope 1+2 / ${Math.round(scope.m2)} m²`,
                  trend: ((sensors.scope1 + sensors.scope2) / scope.m2) < 40 ? 'up' : 'down' }
              : null} />

          <IndicatorCard icon={Lightning} label="Consumo energía primaria"
            benchmark="< 80 kWh/m²·año"
            code="GRI 302-3 · BREEAM ENE 1"
            design={ene1?.energy != null
              ? { value: ene1.energy.toFixed(1), unit: 'kWh/m²·año', note: 'Simulación ENE 1 (energía primaria no renovable)',
                  trend: ene1.energy < 80 ? 'up' : 'down' }
              : null}
            operation={sensors?.energy_kwh > 0 && scope.m2
              ? { value: (sensors.energy_kwh / scope.m2).toFixed(1), unit: 'kWh/m²·año',
                  note: `Sensores año ${sensors.year} · ${Math.round(sensors.energy_kwh).toLocaleString()} kWh totales`,
                  trend: (sensors.energy_kwh / scope.m2) < 80 ? 'up' : 'down' }
              : null} />

          <IndicatorCard icon={Lightning} label="Energía renovable in situ (FV)"
            benchmark="Mix renovable ≥ 30% (Taxonomía UE)"
            code="GRI 302-1 · ESRS E1-5"
            design={null}
            operation={sensors?.energy_renew_kwh > 0
              ? { value: Math.round(sensors.energy_renew_kwh).toLocaleString(), unit: 'kWh/año',
                  note: `Sensores año ${sensors.year}${sensors.electricity_kwh ? ` · ${Math.round(sensors.energy_renew_kwh / (sensors.electricity_kwh + sensors.energy_renew_kwh) * 100)}% del total` : ' (FV autoconsumida)'}`,
                  trend: 'up' }
              : null} />

          <IndicatorCard icon={Lightning} label="Electricidad red (consumida)"
            benchmark="Hotel ES medio: 150-250 kWh/m²·año eléctrico"
            code="GRI 302-1 · GHG Scope 2"
            design={ene1?.energy != null && scope.m2
              ? { value: Math.round(ene1.energy * scope.m2).toLocaleString(), unit: 'kWh/año',
                  note: `Estimación ENE 1 · ${ene1.energy} kWh/m²·año × ${Math.round(scope.m2)} m²` }
              : null}
            operation={sensors?.electricity_kwh > 0
              ? { value: Math.round(sensors.electricity_kwh).toLocaleString(), unit: 'kWh/año',
                  note: `Sensor eléctrico · año ${sensors.year}`, trend: 'up' }
              : null} />

          <IndicatorCard icon={Drop} label="Consumo agua"
            benchmark="Hotel ES medio: 1,5-2 m³/estancia"
            code="GRI 303-3"
            design={ag1?.nivelGlobal != null
              ? { value: ag1.nivelGlobal.toFixed(2), unit: 'nivel global AG 1',
                  note: `${ag1.buildingType || 'Edificio'} · sistema ${ag1.sistemaAguas || 'estándar'}`,
                  trend: ag1.nivelGlobal >= 3 ? 'up' : 'down' }
              : null}
            operation={sensors?.water_m3 > 0
              ? { value: Math.round(sensors.water_m3).toLocaleString(), unit: 'm³/año',
                  note: `Sensor caudal · año ${sensors.year}`, trend: 'up' }
              : null} />

          <IndicatorCard icon={Drop} label="Eficiencia hídrica (AG 1 calculadora)"
            value={ag1?.nivelGlobal != null ? ag1.nivelGlobal.toFixed(2) : (aguaPct ? `${aguaPct.pct}%` : '—')}
            unit={ag1?.nivelGlobal != null ? 'nivel global (0-5)' : 'BREEAM Agua'}
            benchmark="≥ 3 → eficiencia avanzada (BREEAM AG 1)"
            trend={ag1?.nivelGlobal != null ? (ag1.nivelGlobal >= 3 ? 'up' : 'down') : (aguaPct ? (aguaPct.pct >= 55 ? 'up' : 'down') : null)}
            note={ag1?.nivelGlobal != null
              ? `${ag1.buildingType || 'Edificio'} · ${aguaPct?.obtained || '—'}/${aguaPct?.max || 5} pts AG 1`
              : (aguaPct ? `${aguaPct.obtained}/${aguaPct.max} pts` : 'Evalúa AG 1')}
            tag={ag1?.nivelGlobal != null ? 'real' : (aguaPct ? 'proxy' : undefined)}
            code="BREEAM AG 1 · GRI 303-5" />

          <IndicatorCard icon={Recycle} label="Residuos — construcción y operación"
            benchmark="Tasa desvío ≥ 70% (BREEAM mejor práctica)"
            code="BREEAM RSD 1 · GRI 306-3/4/5"
            design={rsd1?.totalResiduos != null
              ? { value: rsd1.totalResiduos.toFixed(1), unit: 't totales obra',
                  note: `RSD 1 · ${rsd1.tasaDesvio?.toFixed(1) || '—'}% desviado de vertedero${rsd1.puntoEjemplar ? ' · Nivel Ejemplar' : ''}`,
                  trend: rsd1.tasaDesvio >= 70 ? 'up' : 'down' }
              : null}
            operation={sensors && (sensors.waste_landfill_t > 0 || sensors.waste_recycled_t > 0)
              ? { value: (sensors.waste_landfill_t || 0).toFixed(1), unit: 't/año a vertedero',
                  note: `Reciclado ${(sensors.waste_recycled_t || 0).toFixed(1)} t · año ${sensors.year}`,
                  trend: sensors.waste_landfill_t < 1 ? 'up' : 'down' }
              : null} />

          <IndicatorCard icon={Recycle} label="Áridos reciclados (RSD 2)"
            value={rsd2?.pct_reciclado != null ? `${rsd2.pct_reciclado}%` : '—'} unit="% áridos no ligados"
            benchmark="≥ 25% → 1 pt · ≥ 50% → Nivel Ejemplar"
            trend={rsd2?.pct_reciclado != null ? (rsd2.pct_reciclado >= 25 ? 'up' : 'down') : null}
            note={rsd2?.reciclado_m3 != null
              ? `${Math.round(rsd2.reciclado_m3).toLocaleString()} m³ reciclados / ${Math.round(rsd2.total_m3).toLocaleString()} m³`
              : 'Carga RSD 2 calculadora'}
            tag={rsd2?.pct_reciclado != null ? 'real' : undefined}
            code="BREEAM RSD 2 · GRI 301-2" />

          <IndicatorCard icon={Recycle} label="Economía circular (BREEAM)"
            value={cirPct ? `${cirPct.pct}%` : '—'} unit="BREEAM Mat + Residuos"
            benchmark="> 50% → contribución sustancial"
            trend={cirPct ? (cirPct.pct >= 50 ? 'up' : 'down') : null}
            note={cirPct ? `${cirPct.obtained}/${cirPct.max} pts (rellena Econ Circular para CI exacto)` : 'Evalúa Materiales y Residuos'}
            tag={cirPct ? 'proxy' : undefined}
            code="BREEAM Mat+RSD · EU Tax 4.4" />

          <IndicatorCard icon={Tree} label="Biodiversidad — plan ecológico"
            value={(() => {
              const u4 = ((evaluations['Uso del Suelo y Ecología-USE 4']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 4']?.points_obtained) ?? 0)
              const u5 = ((evaluations['Uso del Suelo y Ecología-USE 5']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 5']?.points_obtained) ?? 0)
              const u2 = ((evaluations['Uso del Suelo y Ecología-USE 2']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 2']?.points_obtained) ?? 0)
              return (u2 + u4 + u5) > 0 ? `${u2 + u4 + u5}/7` : (usePct ? `${usePct.pct}%` : '—')
            })()}
            unit={(() => {
              const u4 = ((evaluations['Uso del Suelo y Ecología-USE 4']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 4']?.points_obtained) ?? 0)
              const u5 = ((evaluations['Uso del Suelo y Ecología-USE 5']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 5']?.points_obtained) ?? 0)
              const u2 = ((evaluations['Uso del Suelo y Ecología-USE 2']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 2']?.points_obtained) ?? 0)
              return (u2 + u4 + u5) > 0 ? 'pts USE 2+4+5' : 'BREEAM Uso del Suelo'
            })()}
            benchmark="Plan biodiversidad implementado"
            trend={usePct ? (usePct.pct >= 55 ? 'up' : 'down') : null}
            note={(() => {
              const u4 = ((evaluations['Uso del Suelo y Ecología-USE 4']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 4']?.points_obtained) ?? 0)
              if (u4 >= 3) return '✓ Mejora ecológica completa (USE 4 = 3/3)'
              return usePct ? `${usePct.obtained}/${usePct.max} pts categoría` : 'Evalúa USE'
            })()}
            tag={((((evaluations['Uso del Suelo y Ecología-USE 4']?.puntos_auditoria) ?? (evaluations['Uso del Suelo y Ecología-USE 4']?.points_obtained) ?? 0) > 0)) ? 'real' : (usePct ? 'proxy' : undefined)}
            code="BREEAM USE 2/4/5 · GRI 304" />

          <IndicatorCard icon={Wind} label="Prevención contaminación"
            value={(() => {
              const c2 = ((evaluations['Contaminación-CNT 2']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 2']?.points_obtained) ?? 0)
              const c3 = ((evaluations['Contaminación-CNT 3']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 3']?.points_obtained) ?? 0)
              const c4 = ((evaluations['Contaminación-CNT 4']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 4']?.points_obtained) ?? 0)
              return (c2 + c3 + c4) > 0 ? `${c2 + c3 + c4}/9` : (conPct ? `${conPct.pct}%` : '—')
            })()}
            unit={(() => {
              const c2 = ((evaluations['Contaminación-CNT 2']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 2']?.points_obtained) ?? 0)
              return c2 > 0 ? 'pts CNT 2+3+4' : 'BREEAM Contaminación'
            })()}
            benchmark="NOx + escorrentía + lumínica controlados"
            trend={conPct ? (conPct.pct >= 55 ? 'up' : 'down') : null}
            note={(() => {
              const c2 = ((evaluations['Contaminación-CNT 2']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 2']?.points_obtained) ?? 0)
              const c3 = ((evaluations['Contaminación-CNT 3']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 3']?.points_obtained) ?? 0)
              const c4 = ((evaluations['Contaminación-CNT 4']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 4']?.points_obtained) ?? 0)
              const ok = []
              if (c2 >= 3) ok.push('NOx')
              if (c3 >= 5) ok.push('escorrentía')
              if (c4 >= 1) ok.push('lumínica')
              return ok.length ? `✓ ${ok.join(' + ')}` : (conPct ? `${conPct.obtained}/${conPct.max} pts` : 'Evalúa CONT')
            })()}
            tag={((((evaluations['Contaminación-CNT 2']?.puntos_auditoria) ?? (evaluations['Contaminación-CNT 2']?.points_obtained) ?? 0) > 0)) ? 'real' : (conPct ? 'proxy' : undefined)}
            code="BREEAM CNT 2/3/4 · GRI 305-7 · ESRS E2" />

          <IndicatorCard icon={Factory} label="EDCV refrigerantes (CNT 1)"
            value={cnt1?.edcv != null ? cnt1.edcv.toFixed(1) : '—'} unit="kgCO₂e/kW"
            benchmark="< 1.000 kgCO₂e/kW (Tabla 46 BREEAM)"
            trend={cnt1?.edcv != null ? (cnt1.edcv < 1000 ? 'up' : 'down') : null}
            note={cnt1?.pts != null ? `${cnt1.pts}/3 pts CNT 1 · ${cnt1.sistemas} equipos${cnt1.opcion ? ` · opción ${cnt1.opcion}` : ''}` : 'Carga CNT 1 calculadora'}
            tag={cnt1?.edcv != null ? 'real' : undefined}
            code="BREEAM CNT 1 · ODP/GWP refrigerantes" />

        </div>
      </div>

      {/* S — Social */}
      <div style={{ ...CARD, borderLeft: `3px solid ${ACCENT.slate}` }}>
        <PillarHeader letter="S" title="Social" icon={Users} accentColor={ACCENT.slate} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.875rem' }}>

          <IndicatorCard icon={Heartbeat} label="Salud y bienestar (SyB 1-8)"
            value={(() => {
              const codes = ['SyB 1','SyB 2','SyB 3','SyB 4','SyB 5','SyB 6','SyB 7','SyB 8']
              let sum = 0, max = 0, any = false
              codes.forEach(c => {
                const ev = evaluations[`Salud y Bienestar-${c}`]
                if (!ev) return
                any = true
                sum += (ev.puntos_auditoria ?? ev.points_obtained ?? 0)
                max += (ev.points_max ?? 0)
              })
              if (!any) return sybPct ? `${sybPct.pct}%` : '—'
              return `${sum}/${max}`
            })()}
            unit="pts BREEAM Salud"
            benchmark="Confort integral · GRI 403-6 · ESRS S1"
            trend={sybPct ? (sybPct.pct >= 55 ? 'up' : 'down') : null}
            note={(() => {
              const labelMap = {
                'SyB 1':'visual','SyB 2':'IAQ','SyB 3':'térmico','SyB 4':'agua',
                'SyB 5':'acústico','SyB 6':'acceso','SyB 7':'riesgos','SyB 8':'piscinas',
              }
              const tags = []
              Object.entries(labelMap).forEach(([c, l]) => {
                const ev = evaluations[`Salud y Bienestar-${c}`]
                if (ev && ((ev.puntos_auditoria ?? ev.points_obtained ?? 0) > 0)) tags.push(l)
              })
              return tags.length ? `✓ ${tags.join(' · ')}` : (sybPct ? `${sybPct.obtained}/${sybPct.max} pts` : 'Evalúa SyB')
            })()}
            tag={evaluations['Salud y Bienestar-SyB 1'] ? 'real' : (sybPct ? 'proxy' : undefined)}
            code="GRI 403-6 · BREEAM SyB 1-8" />

          <IndicatorCard icon={Wind} label="Calidad aire interior (CO₂)"
            benchmark="< 1.000 ppm (RITE IDA 2 · WELL A01)"
            code="IAQ · WELL Air · BREEAM SyB 2"
            design={(() => {
              const ev = evaluations['Salud y Bienestar-SyB 2']
              const syb2 = (ev?.puntos_auditoria ?? ev?.points_obtained)
              if (syb2 == null) return null
              const audited = ev?.puntos_auditoria != null
              const max = ev?.points_max ?? 3
              const motivoShort = (ev?.motivo_auditoria || '').split('.')[0]
              return { value: `${syb2}/${max}`, unit: 'pts SyB 2',
                       note: audited && motivoShort
                         ? `Auditado por certificador · ${motivoShort}`
                         : (syb2 >= 3 ? '✓ Ventilación + control CO₂ verificados' : 'Cumple Criterio 1 SyB 2'),
                       trend: syb2 >= max ? 'up' : (syb2 > 0 ? null : 'down') }
            })()}
            operation={sensors?.iaq_co2 > 0
              ? { value: Math.round(sensors.iaq_co2).toString(), unit: 'ppm medio',
                  note: `Sensores IAQ · año ${sensors.year}`,
                  trend: sensors.iaq_co2 < 1000 ? 'up' : 'down' }
              : null} />

          <IndicatorCard icon={Bicycle} label="Accesibilidad transporte público (TRA 1)"
            value={(() => {
              if (tra1IA?.ia != null) return `${tra1IA.ia}`
              const ev = evaluations['Transporte-TRA 1']
              const pts = ev ? (ev.puntos_auditoria ?? ev.points_obtained ?? 0) : null
              const max = ev?.points_max ?? 5
              return pts != null ? `${pts}/${max}` : '—'
            })()}
            unit={tra1IA?.ia != null ? 'Índice de Accesibilidad' : 'pts TRA 1'}
            benchmark="IA ≥ 8 (muy buena) · ≥ 4 (buena)"
            trend={(() => {
              if (tra1IA?.ia != null) return tra1IA.ia >= 4 ? 'up' : 'down'
              const ev = evaluations['Transporte-TRA 1']
              const pts = ev ? (ev.puntos_auditoria ?? ev.points_obtained ?? 0) : 0
              return pts > 0 ? 'up' : 'down'
            })()}
            note={(() => {
              if (tra1IA?.ia != null) return tra1IA.ia >= 8 ? 'Muy buena' : tra1IA.ia >= 4 ? 'Buena' : 'Limitada'
              if (tra1IA?.nodos) return `${tra1IA.nodos} nodos analizados · ${tra1IA.tipoEdificio || 'edificio'}`
              return 'Sin transporte público accesible'
            })()}
            tag={tra1IA ? 'real' : undefined}
            code="BREEAM TRA 1 · GRI 203" />

          <IndicatorCard icon={ChartLineUp} label="Movilidad alternativa (TRA 3)"
            value={(() => {
              if (tra3Opt?.fulfilled != null) return `${tra3Opt.fulfilled}/${tra3Opt.total ?? 5}`
              const ev = evaluations['Transporte-TRA 3']
              const pts = ev ? (ev.puntos_auditoria ?? ev.points_obtained ?? 0) : null
              return pts != null ? `${pts}/${ev?.points_max ?? 2}` : '—'
            })()}
            unit={tra3Opt?.fulfilled != null ? 'opciones cumplidas' : 'pts TRA 3'}
            benchmark="≥ 2 opciones → puntuación máxima"
            trend={(() => {
              if (tra3Opt?.fulfilled != null) return tra3Opt.fulfilled >= 2 ? 'up' : 'down'
              const ev = evaluations['Transporte-TRA 3']
              return ev && (ev.puntos_auditoria ?? ev.points_obtained ?? 0) >= (ev.points_max ?? 2) ? 'up' : null
            })()}
            note={tra3Opt?.pct_ve != null ? `${tra3Opt.pct_ve}% plazas VE` : (tra3Opt?.fulfilled != null ? undefined : 'Guarda TRA 3')}
            tag={tra3Opt ? 'real' : undefined}
            code="BREEAM TRA 3" />

          <IndicatorCard icon={Buildings} label="Accesibilidad global transporte"
            value={traPct ? `${traPct.pct}%` : '—'} unit="BREEAM Transporte"
            benchmark="> 55%"
            trend={traPct ? (traPct.pct >= 55 ? 'up' : 'down') : null}
            note={traPct ? `${traPct.obtained}/${traPct.max} pts categoría (TRA 1-5)` : 'Evalúa Transporte'}
            tag={traPct ? 'real' : undefined}
            code="BREEAM TRA · GRI 203" />

          <IndicatorCard icon={ShieldCheck} label="Riesgos climáticos (SyB 7)"
            value={(() => {
              const ev = evaluations['Salud y Bienestar-SyB 7']
              if (!ev) return '—'
              return `${ev.puntos_auditoria ?? ev.points_obtained ?? 0}/${ev.points_max ?? 1}`
            })()}
            unit="pts SyB 7"
            benchmark="Evaluación de vulnerabilidad · TCFD · ESRS E1-3"
            trend={(() => {
              const ev = evaluations['Salud y Bienestar-SyB 7']
              if (!ev) return null
              return (ev.puntos_auditoria ?? ev.points_obtained ?? 0) >= (ev.points_max ?? 1) ? 'up' : 'down'
            })()}
            note={(() => {
              const ev = evaluations['Salud y Bienestar-SyB 7']
              if (!ev) return 'Evalúa SyB 7 · vulnerabilidad ante inundación/viento/sismo'
              const pts = ev.puntos_auditoria ?? ev.points_obtained ?? 0
              return pts > 0 ? '✓ Análisis riesgos naturales acreditado' : 'Sin evaluación de riesgos'
            })()}
            tag={evaluations['Salud y Bienestar-SyB 7'] ? 'real' : undefined}
            code="BREEAM SyB 7 · TCFD · ESRS E1" />

        </div>
      </div>

      {/* G — Gobernanza */}
      <div style={{ ...CARD, borderLeft: `3px solid ${ACCENT.charcoal}` }}>
        <PillarHeader letter="G" title="Gobernanza" icon={ShieldCheck} accentColor={ACCENT.charcoal} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.875rem' }}>

          <IndicatorCard icon={ClipboardText} label="Criterios evaluados"
            value={`${evaluatedCount}/${totalCriteria}`} unit=""
            benchmark="100% = expediente completo"
            trend={evaluatedCount > 0 ? 'up' : null}
            note={`${evidencesPct}% del expediente completado`}
            tag="real" code="BREEAM Cobertura" />

          <IndicatorCard icon={ShieldCheck} label="Categorías completadas"
            value={`${catsDone}/${catsTotal}`} unit="categorías"
            benchmark="Todas las categorías evaluadas"
            trend={catsDone > 0 ? 'up' : null}
            note={catsDone === catsTotal ? '✓ Expediente completo' : `${catsTotal - catsDone} pendientes`}
            tag="real" code="BREEAM Cobertura" />

          <IndicatorCard icon={ChartLineUp} label="Puntuación BREEAM"
            value={`${totalPoints}/${maxPoints}`} unit={`pts · ${breeamPctExact.toFixed(1)}%`}
            benchmark="≥ 55% → Muy Bueno"
            trend={breeamPctExact >= 55 ? 'up' : null}
            note={`Clasificación BREEAM: ${rating}`}
            tag="real" code="BREEAM Score" />

          <IndicatorCard icon={ClipboardText} label="Gestión del proyecto (GST 1-5)"
            value={(() => {
              const codes = ['GST 1','GST 2','GST 3','GST 4','GST 5']
              let sum = 0, max = 0, any = false
              codes.forEach(c => {
                const ev = evaluations[`Gestión-${c}`]
                if (!ev) return
                any = true
                sum += (ev.puntos_auditoria ?? ev.points_obtained ?? 0)
                max += (ev.points_max ?? 0)
              })
              if (!any) return gestPct ? `${gestPct.pct}%` : '—'
              return `${sum}/${max}`
            })()}
            unit="pts GST 1-5"
            benchmark="GST 1 ≥ 3 + GST 2 ≥ 1 + GST 4 ≥ 1"
            trend={gestPct ? (gestPct.pct >= 55 ? 'up' : 'down') : null}
            note={(() => {
              const tags = []
              const g1 = ((evaluations['Gestión-GST 1']?.puntos_auditoria) ?? (evaluations['Gestión-GST 1']?.points_obtained) ?? 0)
              const g2 = ((evaluations['Gestión-GST 2']?.puntos_auditoria) ?? (evaluations['Gestión-GST 2']?.points_obtained) ?? 0)
              const g4 = ((evaluations['Gestión-GST 4']?.puntos_auditoria) ?? (evaluations['Gestión-GST 4']?.points_obtained) ?? 0)
              const g5 = ((evaluations['Gestión-GST 5']?.puntos_auditoria) ?? (evaluations['Gestión-GST 5']?.points_obtained) ?? 0)
              if (g1 >= 3) tags.push('sostenible')
              if (g2 >= 2) tags.push('PCR')
              if (g4 >= 3) tags.push('stakeholders')
              if (g5 >= 3) tags.push('LCC')
              return tags.length ? `✓ ${tags.join(' · ')}` : (gestPct ? `${gestPct.obtained}/${gestPct.max} pts` : 'Evalúa Gestión')
            })()}
            tag={evaluations['Gestión-GST 1'] ? 'real' : (gestPct ? 'proxy' : undefined)}
            code="BREEAM GST · GRI 2 · ESRS G1" />

          <IndicatorCard icon={ChartLineUp} label="Innovación (INO 1)"
            value={(() => {
              const ev = evaluations['Innovación-INO 1']
              if (!ev) return '—'
              return `${ev.puntos_auditoria ?? ev.points_obtained ?? 0}/${ev.points_max ?? 10}`
            })()}
            unit="pts extra · 1% cada uno"
            benchmark="Innovación otorga hasta +10% sobre nota BREEAM"
            trend={(() => {
              const ev = evaluations['Innovación-INO 1']
              if (!ev) return null
              return (ev.puntos_auditoria ?? ev.points_obtained ?? 0) > 0 ? 'up' : null
            })()}
            note={(() => {
              const ev = evaluations['Innovación-INO 1']
              if (!ev) return 'Sin puntos ejemplares acreditados'
              const pts = ev.puntos_auditoria ?? ev.points_obtained ?? 0
              return pts > 0 ? `✓ +${pts}% directo sobre BREEAM Score` : 'Aún sin ejemplares'
            })()}
            tag={evaluations['Innovación-INO 1'] ? 'real' : undefined}
            code="BREEAM INO 1 · bonus directo" />

          <IndicatorCard icon={ClipboardText} label="Ajustes del certificador"
            value={(() => {
              const audited = Object.values(evaluations).filter(ev => ev?.puntos_auditoria != null)
              if (audited.length === 0) return 'Ninguno'
              const rebajados = audited.filter(ev => (ev.puntos_auditoria ?? 0) < (ev.points_obtained ?? 0)).length
              return `${rebajados}/${audited.length}`
            })()}
            unit="créditos ajustados"
            benchmark="Auditoría independiente del certificador BREEAM"
            trend={null}
            note={(() => {
              const audited = Object.entries(evaluations).filter(([_, ev]) => ev?.puntos_auditoria != null)
              const rebajados = audited.filter(([_, ev]) => (ev.puntos_auditoria ?? 0) < (ev.points_obtained ?? 0))
              if (audited.length === 0) return 'Certificador aún no ha revisado'
              if (rebajados.length === 0) return '✓ Sin ajustes a la baja'
              const names = rebajados.map(([k]) => k.split('-')[1]).slice(0, 3).join(' · ')
              return `↓ ${names}${rebajados.length > 3 ? ` y ${rebajados.length - 3} más` : ''}`
            })()}
            tag={Object.values(evaluations).some(ev => ev?.puntos_auditoria != null) ? 'real' : undefined}
            code="BREEAM Auditoría · trazabilidad" />

          <IndicatorCard icon={CalendarCheck} label="Estado certificación"
            value={isCertificationFinalized
              ? 'FINALIZADA'
              : breeamPctExact >= 55 ? 'En proceso'
              : breeamPctExact >= 30 ? 'Inicial'
              : 'Pendiente'}
            unit={isCertificationFinalized ? `${rating} · ${breeamPctExact.toFixed(2)} %` : ''}
            benchmark={isCertificationFinalized
              ? (auditFecha ? `Certificado BREEAM · ${auditFecha}` : 'Certificado BREEAM emitido')
              : 'Certificación formal en Fase 5'}
            trend={isCertificationFinalized ? 'up' : (breeamPctExact >= 30 ? 'up' : null)}
            note={isCertificationFinalized
              ? (auditFecha ? `✓ Informe oficial · fecha auditoría ${auditFecha}` : '✓ Informe BREEAM oficial emitido')
              : undefined}
            tag="real" code="BREEAM Fase" />

        </div>
      </div>

      {/* Leyenda */}
      <div style={{ background: '#FAF7F3', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.625rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <span style={{ fontSize: '0.6rem', background: '#E3EEDF', color: '#2E6E3F', borderRadius: '3px', padding: '1px 5px', fontWeight: '600' }}>SENSOR</span>
            <span style={{ fontSize: '0.7rem', color: '#5C4A3A' }}>Lectura mensual de building_sensors (post-ocupación)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <span style={{ fontSize: '0.6rem', background: '#EAF3EC', color: '#3B7A50', borderRadius: '3px', padding: '1px 5px', fontWeight: '600' }}>DATO REAL</span>
            <span style={{ fontSize: '0.7rem', color: '#5C4A3A' }}>Calculadora BREEAM rellena (ENE 1, MAT 1, etc.)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <span style={{ fontSize: '0.6rem', background: '#FDF8EE', color: '#7A4A10', borderRadius: '3px', padding: '1px 5px', fontWeight: '600' }}>PROXY</span>
            <span style={{ fontSize: '0.7rem', color: '#5C4A3A' }}>% puntos BREEAM en la categoría como aproximación</span>
          </div>
        </div>
        <p style={{ fontSize: '0.75rem', color: '#7A6A5A', margin: 0, lineHeight: '1.6' }}>
          <strong style={{ color: '#5C4A3A' }}>Metodología:</strong> los códigos de cada tarjeta enlazan con el framework de reporte ESG correspondiente (GRI Standards, GHG Protocol, BREEAM ES NC 2015, EU Taxonomy). Botón <em>CSV</em> exporta la tabla maestra; <em>PDF</em> genera un informe imprimible. Los factores de conversión usados: red eléctrica España 2024 = {GRID_FACTOR_ES_2024} kgCO₂e/kWh (REE), gas natural = {GAS_FACTOR} kgCO₂e/kWh (MITECO), residuos a vertedero = {WASTE_LANDFILL_FACTOR} kgCO₂e/kg (DEFRA).
        </p>
      </div>
    </div>
  )
}
